import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, Coins, Shield } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import UserProfileMenu from "@/components/UserProfileMenu";
import { RoleSwitcher } from "@/components/RoleSwitcher";
import { useRole } from "@/hooks/useRole";
import { useCredits } from "@/hooks/useCredits";
import { CartDrawer } from "@/components/CartDrawer";
import PromoBanner from "@/components/PromoBanner";

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { isAuthenticated, signOut } = useAuth();
  const { role, hasMultipleRoles, isAdmin } = useRole();
  const { balance } = useCredits();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);
  const isActive = (path: string) => location.pathname === path;

  const getDashboardPath = () => {
    if (role === 'teacher') return '/teacher/dashboard';
    if (role === 'student') return '/student/dashboard';
    return '/';
  };

  const isDashboardActive = () => {
    return location.pathname === '/teacher/dashboard' || location.pathname === '/student/dashboard';
  };

  const navLinks = [
    { to: "/", label: "Home" },
    ...(isAuthenticated && role ? [{ to: getDashboardPath(), label: "Dashboard" }] : []),
    { to: "/shop", label: "Marketplace" },
    { to: "/blogs", label: "Blogs" },
    { to: "/premium", label: "Premium" },
    ...(isAuthenticated ? [{ to: "/results", label: "Results" }] : []),
    ...(isAuthenticated && isAdmin ? [{ to: "/admin/upload", label: "Admin", icon: Shield }] : []),
  ];

  return (
    <>
      <PromoBanner />
      <header className={`sticky top-0 w-full z-40 transition-all duration-300 ${
        scrolled ? 'bg-background/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}>
      <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2 overflow-hidden">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 z-10">
          <div className="flex flex-col leading-none">
            <span className="text-2xl font-black tracking-tight text-foreground">EduLabs</span>
            <span className="text-[9px] text-muted-foreground tracking-wide">by ConceptLabs</span>
          </div>
        </Link>

        {/* Center Nav - Pill container like Bumble */}
        <nav className="hidden lg:flex items-center">
          <div className="flex items-center bg-card/80 backdrop-blur-sm rounded-full border px-2 py-1.5 gap-1">
            {navLinks.map((link) => (
              <Link key={link.to} to={link.to}>
                <button className={`px-5 py-2 text-sm font-semibold rounded-full transition-all duration-200 ${
                  isActive(link.to) || (link.label === 'Dashboard' && isDashboardActive())
                    ? 'bg-foreground text-background'
                    : 'text-foreground/70 hover:text-foreground hover:bg-muted'
                }`}>
                  {link.label}
                </button>
              </Link>
            ))}
          </div>
        </nav>

        {/* Right side */}
        <div className="hidden lg:flex items-center gap-3 z-10">
          {isAuthenticated ? (
            <div className="flex items-center gap-2">
              <Link to="/premium" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted text-sm font-semibold text-foreground hover:bg-muted/80 transition-colors">
                <Coins className="h-4 w-4 text-primary" />
                <span>{balance ?? 0}</span>
              </Link>
              <CartDrawer />
              {hasMultipleRoles && <RoleSwitcher />}
              <UserProfileMenu />
            </div>
          ) : (
            <>
              <Link to="/auth">
                <Button className="rounded-full px-6 bg-foreground text-background hover:bg-foreground/90 font-semibold">
                  Sign in
                </Button>
              </Link>
            </>
          )}
        </div>

        {/* Mobile */}
        <div className="lg:hidden flex items-center flex-shrink-0">
          <Button variant="ghost" size="icon" onClick={toggleMenu} className="h-9 w-9 rounded-full flex-shrink-0">
            {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </Button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden absolute inset-x-0 top-full z-50 bg-card border-b shadow-lg">
          <div className="container mx-auto px-4 py-5 flex flex-col gap-2">
            {navLinks.map((link) => (
              <Link key={link.to} to={link.to} onClick={closeMenu}>
                <button className={`w-full text-left px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${
                  isActive(link.to) ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted'
                }`}>
                  {link.label}
                </button>
              </Link>
            ))}

            {!isAuthenticated && (
              <div className="pt-4 flex flex-col gap-3 border-t mt-2">
                <Button className="w-full rounded-full bg-foreground text-background hover:bg-foreground/90" asChild>
                  <Link to="/auth" onClick={closeMenu}>Sign In</Link>
                </Button>
              </div>
            )}

            {isAuthenticated && (
              <div className="pt-4 border-t mt-2 space-y-3">
                <div className="flex items-center gap-2">
                  <Link to="/premium" onClick={closeMenu} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted text-sm font-semibold text-foreground hover:bg-muted/80 transition-colors">
                    <Coins className="h-4 w-4 text-primary" />
                    <span>{balance ?? 0}</span>
                  </Link>
                  <CartDrawer />
                  <UserProfileMenu />
                </div>
                {hasMultipleRoles && (
                  <div>
                    <RoleSwitcher />
                  </div>
                )}
                <Button variant="destructive" className="w-full rounded-full" onClick={() => { signOut(); closeMenu(); }}>
                  Sign Out
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
      </header>
    </>
  );
};

export default Navbar;
